import { NavigateFunction } from "react-router-dom";
import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

export interface DailyStatRow {
  date: string;
  event_type: string;
  total: number;
}

export interface DailyStatsResponse {
  days: number;
  worker_id: string | null;
  data: DailyStatRow[];
}

export interface ByPluginRow {
  worker_id: string;
  event_type: string;
  total: number;
}

export interface WorkerInfo {
  subscription_id?: number | null;
  user_id?: string | null;
  subscription_type?: string | null;
  user_name?: string | null;
  /**
   * "directory" = nom resolu via Keycloak pour un worker offline (Redis vide).
   * Absent / "heartbeat" = nom issu du heartbeat plugin (worker en ligne).
   */
  user_name_source?: "heartbeat" | "directory" | null;
  plugin_version?: string | null;
  browser_name?: string | null;
  browser_version?: string | null;
  os_name?: string | null;
  last_seen?: string | null;
  is_anonymous?: boolean | null;
  ip?: string | null;
  location?: { city?: string; country?: string; [k: string]: unknown } | null;
  linkedin_public_identifier?: string | null;
  // Contexte B2B (renseigné seulement si le worker est rattaché à une organisation).
  // Pour le badge "type d'abonnement", organization_tier (+ is_business) PRIME sur
  // subscription_type legacy : une org business a subscription_type='pro' backfillé,
  // d'où le "PRO" affiché à tort -> on affiche le tier orga à la place.
  organization_tier?: string | null; // "starter" | "business" | "enterprise"
  is_business?: boolean | null;
  organization_name?: string | null;
  member_role?: "OrgAdmin" | "OrgUser" | "PendingUser" | string | null;
}

export interface ByPluginResponse {
  days: number;
  data: ByPluginRow[];
  workers?: Record<string, WorkerInfo>;
}

export interface TotalsResponse {
  days: number;
  totals: Record<string, number>;
}

export async function getScrapingDailyStats(
  navigate: NavigateFunction,
  params?: { days?: number; worker_id?: string }
): Promise<DailyStatsResponse> {
  const q = new URLSearchParams();
  if (params?.days) q.set("days", String(params.days));
  if (params?.worker_id) q.set("worker_id", params.worker_id);
  const url = `${API_URL}/admin/scraping-stats/daily${q.toString() ? `?${q}` : ""}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des stats de scraping");
  return res.json();
}

export async function getScrapingByPlugin(
  navigate: NavigateFunction,
  params?: { days?: number }
): Promise<ByPluginResponse> {
  const q = new URLSearchParams();
  if (params?.days) q.set("days", String(params.days));
  const url = `${API_URL}/admin/scraping-stats/by-plugin${q.toString() ? `?${q}` : ""}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des stats par plugin");
  return res.json();
}

export async function getScrapingTotals(
  navigate: NavigateFunction,
  params?: { days?: number }
): Promise<TotalsResponse> {
  const q = new URLSearchParams();
  if (params?.days) q.set("days", String(params.days));
  const url = `${API_URL}/admin/scraping-stats/totals${q.toString() ? `?${q}` : ""}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des totaux");
  return res.json();
}

// Débit de scraping temps réel (rolling-60s). Une valeur par event_type =
// nombre de scrapes sur les 60 dernières secondes au pas `step`.
export interface ThroughputPoint {
  ts: number; // epoch secondes
  invitation: number;
  message: number;
  candidate: number;
  company: number;
  school: number;
  search: number;
  total: number;
}

export interface ThroughputResponse {
  window: number; // fenêtre demandée (s)
  step: number; // pas d'échantillonnage (s)
  rate_window: number; // fenêtre glissante du taux (s, = 60)
  worker_id: string | null;
  points: ThroughputPoint[];
}

export async function getScrapingThroughput(
  navigate: NavigateFunction,
  params?: { window?: number; worker_id?: string }
): Promise<ThroughputResponse> {
  const q = new URLSearchParams();
  if (params?.window) q.set("window", String(params.window));
  if (params?.worker_id) q.set("worker_id", params.worker_id);
  const url = `${API_URL}/admin/scraping-stats/throughput${q.toString() ? `?${q}` : ""}`;
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement du débit de scraping");
  return res.json();
}
